import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const defaultVideoUrl = 'https://pub-856ba7aa61aa48e7a455614a46820b28.r2.dev/Example.mp4';
const defaultPosterUrl = 'https://pub-856ba7aa61aa48e7a455614a46820b28.r2.dev/imageref.jpg';
const droneVideoUrl = 'https://pub-856ba7aa61aa48e7a455614a46820b28.r2.dev/N%C2%B04%20Azalea%20Bay%20Apartments%20copie.mp4';
const dronePosterUrl = 'https://pub-856ba7aa61aa48e7a455614a46820b28.r2.dev/videoframe_372.png';

const useCases1 = ['Listings', 'MLS', 'Website', 'Socials'];
const useCases2 = ['Listings', 'MLS', 'Website', 'Socials'];

const ProductShowcase: React.FC = () => {
  return (
    <section id="what-you-get" className="py-8 sm:py-12 md:py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <motion.h2
            className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            What You Get
          </motion.h2>
        </div>
        <div className="max-w-7xl mx-auto">
          <div className="space-y-6 lg:space-y-8">
            {/* Card 1: Matterport — video left */}
            <motion.div
              className="card-hover bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="w-full lg:w-1/2 p-4 sm:p-5 lg:p-6">
                  <div className="aspect-video rounded-2xl overflow-hidden bg-gray-900">
                    <video
                      className="w-full h-full object-cover rounded-2xl shadow-lg"
                      controls
                      playsInline
                      preload="metadata"
                      poster={defaultPosterUrl}
                      src={defaultVideoUrl}
                    />
                  </div>
                </div>
                <div className="w-full lg:w-1/2 p-5 sm:p-6 lg:p-8 flex flex-col gap-5">
                  <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-gray-900 leading-tight text-center">
                    Matterport video
                  </h2>
                  <ul className="space-y-2.5">
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                      <span className="text-base sm:text-lg text-gray-700">Walkthrough video from your Matterport tour</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                      <span className="text-base sm:text-lg text-gray-700">Unbranded version included (MLS-ready)</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                      <span className="text-base sm:text-lg text-gray-700">Full HD video</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                      <span className="text-base sm:text-lg text-gray-700">Delivered in 24h (Mon–Thu)</span>
                    </li>
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    {useCases1.map((useCase, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-600 font-medium"
                      >
                        {useCase}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Card 2: Matterport + drone — video right */}
            <motion.div
              className="card-hover bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex flex-col lg:flex-row-reverse lg:items-center">
                <div className="w-full lg:w-1/2 p-4 sm:p-5 lg:p-6">
                  <div className="aspect-video rounded-2xl overflow-hidden bg-gray-900">
                    <video
                      className="w-full h-full object-cover rounded-2xl shadow-lg"
                      controls
                      playsInline
                      preload="metadata"
                      poster={dronePosterUrl}
                      src={droneVideoUrl}
                    />
                  </div>
                </div>
                <div className="w-full lg:w-1/2 p-5 sm:p-6 lg:p-8 flex flex-col gap-5">
                  <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-gray-900 leading-tight text-center">
                    Matterport + drone clips
                  </h2>
                  <ul className="space-y-2.5">
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                      <span className="text-base sm:text-lg text-gray-700">Full edited montage: Matterport video + your drone clips</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                      <span className="text-base sm:text-lg text-gray-700">Unbranded version included (MLS-ready)</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                      <span className="text-base sm:text-lg text-gray-700">Full HD video</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                      <span className="text-base sm:text-lg text-gray-700">Delivered in 24h (Mon–Thu)</span>
                    </li>
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    {useCases2.map((useCase, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-600 font-medium"
                      >
                        {useCase}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
